import * as React from 'react';
import { Text, createStyles } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { openConfirmModal } from '@mantine/modals';

// Hooks
import { useStaticKey } from '../../hooks/useStaticKey';

// Local components
import { FormScaffolding } from '../../FormScaffolding';

// Helpers
import { hydrateManifestSection } from '../../helpers/hydrateManifestSection';

// Contexts
import { AuthContext } from '../../../../contexts/Auth';

// Types
import { IContactRecord } from '../../../../types';

const useStyles = createStyles((theme) => {
    return {};
});

interface ConfirmPrimaryContactSlideProps {
    content?: string | React.ReactNode;
    data: Record<string, any>;
    ancillaryData?: Record<string, any>;
    onConfirm: () => void;
    onChange: (val: string) => void;
    formId?: string;
    clientId?: string;
    surveyId?: string;
    trigger?: string;
    ressignPrimaryPending?: boolean;
}

export const ConfirmPrimaryContactSlide: React.FC<ConfirmPrimaryContactSlideProps> = ({
    content,
    onConfirm,
    onChange,
    data,
    ancillaryData,
    formId,
    clientId,
    surveyId,
    trigger,
    ressignPrimaryPending,
    ...props
}) => {
    const { t } = useTranslation();
    const { t: componentTranslations } = useTranslation('components', { keyPrefix: 'confirmPrimaryContactSlide' });
    const { getStaticKeyByName } = useStaticKey();
    const { authState } = React.useContext(AuthContext);
    const { classes, cx } = useStyles();
    const manifest = React.useMemo(
        () =>
            hydrateManifestSection([
                { id: 'ORG0013' },
                {
                    id: 'CON0000',
                    collapsible: true,
                    description: componentTranslations('description2') ?? '',
                    subManifest: [
                        // this is the contact fields relevant in this context
                        { id: 'CON0001' },
                        { id: 'CON0002' },
                        { id: 'CON0003' },
                        { id: 'CON0022' },
                        { id: 'CON0023' },
                        { id: 'CON0024' },
                        {},
                        { id: 'CON0019' },
                        { id: 'CON0020' },
                        {},
                        { id: 'CON0007' },
                        { id: 'CON0008' },
                        { id: 'CON0009' },
                        {},
                        { id: 'CON0005' },
                        { id: 'CON0006' },
                        {},
                        { id: 'CON0013' },
                        { id: 'CON0011' },
                        { id: 'CON0017' },
                        { id: 'CON0018' },
                        {},
                        { id: 'CON0016' }
                    ]
                }
            ]),
        [hydrateManifestSection, componentTranslations]
    );
    const openConfirmChangeModal = React.useCallback(({ confirmCallback }: { confirmCallback: () => void }) => {
        openConfirmModal({
            title: componentTranslations('modal.title'),
            children: (
                <Text size="sm" mt="sm" mb="xl">
                    {componentTranslations('modal.body')}
                </Text>
            ),
            labels: {
                confirm: componentTranslations('modal.confirmCTA'),
                cancel: t('cancel', { ns: 'common' })
            },
            closeOnConfirm: true,
            confirmProps: { color: 'red' },
            onConfirm: () => {
                confirmCallback();
            }
        });
    }, []);
    const contacts = (ancillaryData?.contacts ?? []) as IContactRecord[];
    const modifiedState = React.useMemo(() => {
        return Object.hasOwn(data, 'ORG0013')
            ? data
            : {
                  ORG0013:
                      contacts.find((contact) => contact?.id === ancillaryData?.organization?.primaryContact)?.id ?? '' // Temp?
                  //   ORG0013: ancillaryData?.organization?.primaryContact
              };
    }, [data, ancillaryData]);

    return (
        <FormScaffolding
            data={modifiedState}
            {...{ ancillaryData, formId, trigger }}
            progressiveForm={false}
            dirtySaveOnly={false}
            clientId={clientId}
            isSubForm
            noCardStyle
            sections={[
                {
                    name: componentTranslations('name') ?? '',
                    description: componentTranslations('description') ?? '',
                    manifest
                }
            ]}
            onSave={async ({ values }) => {
                if (values[getStaticKeyByName('primaryContact')] === authState?.contactId) {
                    onConfirm();
                } else {
                    openConfirmChangeModal({
                        confirmCallback: () => onChange(values[getStaticKeyByName('primaryContact')])
                    });
                }
            }}
            externalIsLoading={ressignPrimaryPending}
            saveLabelKey={'confirmSelection'}
        />
    );
};
